import React from 'react';
import { Link } from 'react-router-dom';
import { Wine, Truck, FileText, Warehouse, ArrowRight } from 'lucide-react';

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section - Minimalist with large logo */}
      <section className="relative bg-white py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/logo.png" 
                alt="Wine Collection & Spirits" 
                className="w-80 mb-8"
              />
              <h1 className="text-5xl font-light text-gray-900 mb-6" data-testid="hero-title">
                Premium Wine & Spirits Import Distribution and Logistics
              </h1>
              <p className="text-xl text-gray-600 mb-8 font-light">
                Miami's trusted partner for premium wine and spirits import, storage, and distribution
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white font-light hover:bg-gray-800 transition"
                  data-testid="browse-products-btn"
                >
                  Browse Products
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-8 py-3 border border-gray-900 text-gray-900 font-light hover:bg-gray-50 transition"
                  data-testid="learn-services-btn"
                >
                  Our Services
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1578911373434-0cb395d2cbfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHx3aW5lJTIwYm90dGxlc3xlbnwwfHx8fDE3NzUwMTEyNzl8MA&ixlib=rb-4.1.0&q=85"
                alt="Premium Wine Collection"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Warehouse Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">Our Facility</h2>
            <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto">
              4,500 sq. ft. temperature-controlled storage ensuring optimal preservation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="group overflow-hidden">
              <img
                src="https://images.pexels.com/photos/5720872/pexels-photo-5720872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Wine barrel storage in temperature-controlled cellar"
                className="w-full h-80 object-cover grayscale group-hover:grayscale-0 transition duration-500"
              />
              <div className="mt-4">
                <h3 className="text-lg font-light text-gray-900">Temperature Control</h3>
                <p className="text-sm text-gray-600 font-light">Climate-optimized storage</p>
              </div>
            </div>

            <div className="group overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1714650601435-67a4d51a0798?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBzdG9yYWdlJTIwcmFja3N8ZW58MHx8fHwxNzc1MDExMjQ5fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional warehouse storage racks"
                className="w-full h-80 object-cover grayscale group-hover:grayscale-0 transition duration-500"
              />
              <div className="mt-4">
                <h3 className="text-lg font-light text-gray-900">Professional Storage</h3>
                <p className="text-sm text-gray-600 font-light">Organized inventory systems</p>
              </div>
            </div>

            <div className="group overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBmb3JrbGlmdHxlbnwwfHx8fDE3NzUwMTEyNzR8MA&ixlib=rb-4.1.0&q=85"
                alt="Warehouse forklift operations"
                className="w-full h-80 object-cover grayscale group-hover:grayscale-0 transition duration-500"
              />
              <div className="mt-4">
                <h3 className="text-lg font-light text-gray-900">Logistics Operations</h3>
                <p className="text-sm text-gray-600 font-light">Efficient handling & delivery</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Minimalist */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light text-gray-900 mb-8">About Us</h2>
          <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
            Miami-based wine and spirits import and distribution company specializing in premium selections
            from around the world, with a strong focus on French and Italian wines.
          </p>
          <p className="text-lg text-gray-600 font-light leading-relaxed">
            Operating from our temperature-controlled facility at 7227 NW 32 street Miami FL 33122,
            we ensure optimal preservation and handling of all products from arrival to final delivery.
          </p>
        </div>
      </section>

      {/* Services Overview - Minimalist Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">What We Offer</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Wine size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">Premium Selection</h3>
              <p className="text-sm text-gray-600 font-light">Premium wines & spirits worldwide</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Warehouse size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">Climate Storage</h3>
              <p className="text-sm text-gray-600 font-light">Temperature-controlled facility</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Truck size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">Reliable Delivery</h3>
              <p className="text-sm text-gray-600 font-light">Florida-wide distribution</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <FileText size={48} className="text-gray-900" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">Full Compliance</h3>
              <p className="text-sm text-gray-600 font-light">Clearing & regulatory support</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Minimalist Two Column */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="border border-gray-200 p-12">
              <h3 className="text-3xl font-light text-gray-900 mb-6">For B2B Clients</h3>
              <p className="text-gray-600 font-light mb-8">
                Restaurants, Hotels, and Liquor Stores: Access our premium catalog and place orders with ease.
              </p>
              <ul className="space-y-4 mb-10 text-gray-600 font-light">
                <li>Easy online ordering</li>
                <li>Competitive wholesale pricing</li>
                <li>Reliable Florida delivery</li>
              </ul>
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-gray-900 text-white font-light hover:bg-gray-800 transition"
                data-testid="contact-client-btn"
              >
                Contact Us
              </Link>
            </div>

            <div className="border border-gray-200 p-12 bg-gray-50">
              <h3 className="text-3xl font-light text-gray-900 mb-6">Logistique Solution for Brands</h3>
              <h4 className="text-xl font-light text-gray-700 mb-6">Clearing Solution</h4>
              <p className="text-gray-600 font-light mb-8">
                Complete turnkey logistics and clearing services for brands entering the Florida market.
              </p>
              <ul className="space-y-4 mb-10 text-gray-600 font-light">
                <li>Import & customs clearance</li>
                <li>Temperature-controlled warehousing</li>
                <li>Licensed storage & distribution</li>
                <li>Regulatory compliance support</li>
                <li>Complete logistics management</li>
              </ul>
              <div className="flex gap-4">
                <Link
                  to="/brand-registration"
                  className="inline-block px-8 py-3 bg-gray-900 text-white font-light hover:bg-gray-800 transition"
                  data-testid="brand-register-btn"
                >
                  Register Your Brand
                </Link>
                <Link
                  to="/contact"
                  className="inline-block px-8 py-3 border border-gray-900 text-gray-900 font-light hover:bg-gray-100 transition"
                  data-testid="partner-inquiry-btn"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
